import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import { Api, FlowApi } from 'api';
import { ENVIRONMENT } from 'config/settings';
import { createContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import jwt_decode from 'jwt-decode';

export const SignalRConnectionContext = createContext();

const GetHubInfo = async (hubName, userId, userRole) => {
  switch (hubName) {
    case 'LessonHub':
      return await FlowApi.getSignalRConnectionInfo(userId, hubName, userRole);
    case 'SignalRHub':
      return await Api.getSignalRConnectionInfo(userId, hubName, userRole);
    default:
      throw new Error(`${hubName} not implemented`);
  }
};

export const SignalRProvider = ({ hubName, children, context }) => {
  const { userId, userRole, subscriptionStatus, schoolIds } = useSelector(
    ({ user }) => ({
      userId: user.userId,
      userRole: user.userRole,
      subscriptionStatus: user?.subscription?.subscriptionStatus,
      schoolIds: user?.students?.map(s => s.schoolId) ?? []
    })
  );
  const [connection, setConnection] = useState(null);
  const connectionInfoRef = useRef({
    calls: 0,
    lastConnectionTime: null,
    lastUserId: null,
    setupInProgress: false
  });
  const connectionRef = useRef(null);
  const joinedSchoolGroupsRef = useRef(new Set());

  useEffect(() => {
    console.warn('[SignalR - Connection] Effect triggered with:', {
      hubName,
      userId,
      userRole,
      schoolIds: schoolIds?.length,
      currentConnection: connectionRef.current?.state,
      setupInProgress: connectionInfoRef.current.setupInProgress,
      joinedGroups: [...joinedSchoolGroupsRef.current]
    });

    // Reset joined groups when userId changes
    if (userId !== connectionInfoRef.current.lastUserId) {
      console.warn(
        '[SignalR - Connection] New user detected, resetting joined school groups'
      );
      joinedSchoolGroupsRef.current = new Set();
      connectionInfoRef.current.lastUserId = userId;
    }

    async function createSignalRConnection() {
      // Prevent multiple simultaneous connection attempts
      if (connectionInfoRef.current.setupInProgress) {
        console.warn(
          '[SignalR - Connection] Setup already in progress, skipping'
        );
        return;
      }

      // Don't create new connection if we already have a valid one
      if (!userId) {
        console.warn(
          '[SignalR - Connection] No userId available, skipping connection setup'
        );
        return;
      }
      if (connectionRef.current?.state === HubConnectionState.Connected) {
        console.warn(
          '[SignalR - Connection] Active connection exists, skipping setup'
        );
        return;
      }

      // Prevent rapid reconnection attempts
      const now = Date.now();
      if (
        connectionInfoRef.current.lastConnectionTime &&
        now - connectionInfoRef.current.lastConnectionTime < 5000
      ) {
        // 5 second cooldown
        console.warn(
          '[SignalR - Connection] Connection attempt too soon after last attempt, skipping'
        );
        return;
      }

      connectionInfoRef.current.setupInProgress = true;
      connectionInfoRef.current.lastConnectionTime = now;

      try {
        let isValid = true;

        // We only want users with active susbcriptions to connect to live lessons.
        if (hubName === 'LessonHub') {
          isValid =
            subscriptionStatus === 'active' ||
            subscriptionStatus === 'trialing';
        }

        console.warn('[SignalR - Connection] Subscription validation:', {
          hubName,
          isValid,
          subscriptionStatus
        });

        if (
          userId &&
          connectionInfoRef?.current?.userId !== userId &&
          isValid
        ) {
          // Stop existing connection if any
          if (connectionRef.current) {
            console.warn(
              '[SignalR - Connection] Stopping existing connection before creating new one'
            );
            try {
              await connectionRef.current.stop();
              console.warn(
                '[SignalR - Connection] Successfully stopped existing connection'
              );
            } catch (e) {
              console.warn(
                '[SignalR - Connection] Error stopping existing connection:',
                e
              );
            }
          }

          console.warn(
            '[SignalR - Connection] Fetching connection info for hub:',
            hubName
          );
          let info = await GetHubInfo(hubName, userId, userRole);
          console.warn('[SignalR - Connection] Retrieved connection info:', {
            url: info.url,
            userId,
            userRole
          });

          const options = {
            accessTokenFactory: async () => {
              const token = jwt_decode(info.accessToken);
              const expiry = new Date(token.exp * 1000);
              const msDifference = expiry - new Date();
              const minutes = Math.floor(msDifference / 1000 / 60);

              // If we're less than 30 minutes from expiry, refetch the token
              if (minutes <= 30) {
                console.warn(
                  '[SignalR - Connection] Token expiring soon, refreshing'
                );
                info = await GetHubInfo(hubName, userId, userRole);
              }

              return info.accessToken;
            }
          };

          connectionInfoRef.current = {
            ...connectionInfoRef.current,
            ...info,
            userId
          };

          console.warn('[SignalR - Connection] Building new connection');
          const newConnection = new HubConnectionBuilder()
            .withUrl(info.url, options)
            .withAutomaticReconnect()
            .configureLogging(
              ENVIRONMENT === 'prod' ? LogLevel.Error : LogLevel.Debug
            )
            .build();

          // Store connection in ref before starting
          connectionRef.current = newConnection;
          console.warn('[SignalR - Connection] Attempting to start connection');

          try {
            await newConnection.start();
            console.warn(
              '[SignalR - Connection] Successfully started connection'
            );
            setConnection(newConnection);

            // School groups join logic
            console.warn(
              '[SignalR - Connection] Checking school groups to join',
              {
                currentGroups: [...joinedSchoolGroupsRef.current],
                targetGroups: schoolIds.map(id => `SchoolGroup_${id}`)
              }
            );

            const joinPromises = schoolIds
              .map(schoolId => `SchoolGroup_${schoolId}`)
              .filter(groupId => !joinedSchoolGroupsRef.current.has(groupId))
              .map(async groupId => {
                try {
                  await Api.signalRJoinSchoolGroups(userId, groupId);
                  joinedSchoolGroupsRef.current.add(groupId);
                  console.warn(
                    '[SignalR - Connection] Successfully joined school group:',
                    groupId
                  );
                } catch (e) {
                  console.error(
                    '[SignalR - Connection] Failed to join school group:',
                    groupId,
                    e
                  );
                }
              });

            if (joinPromises.length > 0) {
              console.warn(
                `[SignalR - Connection] Joining ${joinPromises.length} new school groups`
              );
              await Promise.all(joinPromises);
            } else {
              console.warn(
                '[SignalR - Connection] No new school groups to join'
              );
            }

            // Clean up any joined groups that are no longer in schoolIds
            const currentGroupIds = new Set(
              schoolIds.map(id => `SchoolGroup_${id}`)
            );
            const groupsToRemove = [...joinedSchoolGroupsRef.current].filter(
              groupId => !currentGroupIds.has(groupId)
            );

            if (groupsToRemove.length > 0) {
              console.warn(
                '[SignalR - Connection] Cleaning up old school group joins:',
                groupsToRemove
              );
              for (const groupId of groupsToRemove) {
                joinedSchoolGroupsRef.current.delete(groupId);
              }
            }
          } catch (e) {
            console.error(
              '[SignalR - Connection] Failed to start connection:',
              e
            );
            connectionRef.current = null;
            setConnection(null);
          }
        } else {
          console.warn(
            '[SignalR - Connection] Skipping connection setup - conditions not met:',
            {
              hasUserId: !!userId,
              userIdChanged: connectionInfoRef?.current?.userId !== userId,
              isValid
            }
          );
        }
      } catch (e) {
        console.error(
          '[SignalR - Connection] Error in connection setup process:',
          e
        );
        connectionRef.current = null;
        setConnection(null);
      } finally {
        connectionInfoRef.current.setupInProgress = false;
      }
    }

    if (userRole === 'Student') {
      console.warn(
        '[SignalR - Connection] Initiating connection setup for student'
      );
      createSignalRConnection();
    } else {
      console.warn(
        '[SignalR - Connection] Skipping connection setup - not a student user'
      );
    }

    return () => {
      // Only stop connection if component is truly unmounting
      console.warn('[SignalR - Connection] Cleanup triggered', {
        hasUserId: !!userId,
        hasConnection: !!connectionRef.current,
        joinedGroups: [...joinedSchoolGroupsRef.current]
      });

      if (!userId && connectionRef.current) {
        console.warn(
          '[SignalR - Connection] Cleanup - stopping connection due to component unmount'
        );
        connectionRef.current.stop().catch(e => {
          console.warn(
            '[SignalR - Connection] Error stopping connection during cleanup:',
            e
          );
        });
        connectionRef.current = null;
        joinedSchoolGroupsRef.current.clear();
      } else {
        console.warn(
          '[SignalR - Connection] Cleanup - keeping connection alive'
        );
      }
    };
  }, [hubName, schoolIds, subscriptionStatus, userId, userRole]);

  const Context = context || SignalRConnectionContext;

  return <Context.Provider value={connection}>{children}</Context.Provider>;
};
